(() => {
  const SELECTORS = {
    itemToggle: '[data-subscription-item-toggle]',
    globalToggle: '[data-subscription-cart-toggle]',
    globalPanel: '[data-subscription-cart-panel]',
    globalOptions: '[data-subscription-cart-options]',
    globalOptionButton: '[data-subscription-cart-option]',
    globalCancel: '[data-subscription-cart-cancel]',
    itemEditor: '[data-subscription-item-editor]',
    itemPanel: '[data-subscription-item-panel]',
    itemCancel: '[data-subscription-cancel]',
    modalOptionButton: '[data-subscription-option]',
    itemUsps: '[data-subscription-item-usps]',
    itemCommitmentNote: '[data-subscription-item-commitment-note]',
  };

  const findItemUspsTemplate = (editor, planKey) => {
    const key = (planKey || '').trim();
    if (!key) return null;
    const templates = editor.querySelectorAll('template[data-subscription-item-usps-template]');
    return (
      Array.from(templates).find(
        (tpl) => tpl.getAttribute('data-subscription-item-usps-template') === key
      ) || null
    );
  };

  const findItemCommitmentNoteTemplate = (editor, planKey) => {
    const key = (planKey || '').trim();
    if (!key) return null;
    const templates = editor.querySelectorAll('template[data-subscription-item-commitment-note-template]');
    return (
      Array.from(templates).find(
        (tpl) => tpl.getAttribute('data-subscription-item-commitment-note-template') === key
      ) || null
    );
  };

  const updateItemEditorUsps = (panel, planKey) => {
    const editor = panel?.closest(SELECTORS.itemEditor);
    if (!editor || !planKey) return;
    const root = editor.querySelector(SELECTORS.itemUsps);
    if (!root) return;
    const tpl = findItemUspsTemplate(editor, planKey);
    if (!tpl) {
      root.innerHTML = '';
      return;
    }
    root.innerHTML = '';
    root.appendChild(tpl.content.cloneNode(true));
  };

  const updateItemEditorCommitmentNote = (panel, planKey) => {
    const editor = panel?.closest(SELECTORS.itemEditor);
    if (!editor || !planKey) return;
    const root = editor.querySelector(SELECTORS.itemCommitmentNote);
    if (!root) return;
    const tpl = findItemCommitmentNoteTemplate(editor, planKey);
    const noteText = tpl ? tpl.content.textContent.trim() : '';
    root.textContent = noteText;
    root.hidden = noteText === '';
  };

  const setBusy = (element, isBusy) => {
    if (!element) return;
    element.disabled = isBusy;
    element.classList.toggle('processing', isBusy);
  };

  const getLineItemLoadingTarget = (panel, optionButton) => {
    if (!panel || !optionButton) return null;
    const isOnetime = (optionButton.dataset.sellingPlanId || '') === '';
    if (isOnetime) {
      return panel.querySelector('.subscription-item-panel--onetime');
    }
    return panel.querySelector('.subscription-item-panel__options');
  };

  const setLoadingOverlay = (target, isLoading) => {
    if (!target) return;
    target.classList.toggle('is-loading', isLoading);
  };

  const updateLineItem = ({ line, quantity, sellingPlanId, variantId, subscriptionVariantId, itemKey }) => {
    const needsVariantSwap =
      Boolean(sellingPlanId) &&
      subscriptionVariantId &&
      String(variantId) !== String(subscriptionVariantId);

    if (needsVariantSwap) {
      return fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          id: itemKey || undefined,
          line: itemKey ? undefined : line,
          quantity: 0,
        }),
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error('Failed to remove cart line item');
          }
          return response.json();
        })
        .then(() =>
          fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({
              items: [
                {
                  id: Number(subscriptionVariantId),
                  quantity,
                  selling_plan: Number(sellingPlanId),
                },
              ],
            }),
          })
        )
        .then(async (response) => {
          if (!response.ok) {
            const errorPayload = await response.json().catch(() => ({}));
            throw new Error(errorPayload.description || 'Failed to add subscription variant');
          }
          return response.json();
        });
    }

    return fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        line,
        quantity,
        selling_plan: sellingPlanId || '',
      }),
    }).then(async (response) => {
      if (!response.ok) {
        const errorPayload = await response.json().catch(() => ({}));
        throw new Error(errorPayload.description || 'Failed to update cart line item');
      }
      return response.json();
    });
  };

  const getEligibleItemEditors = () => {
    return Array.from(document.querySelectorAll(SELECTORS.itemEditor)).filter((editor) => {
      return editor.querySelector(`${SELECTORS.itemPanel} [data-subscription-option][data-selling-plan-id]:not([data-selling-plan-id=""])`);
    });
  };

  const getCommonPlanOptions = () => {
    const editors = getEligibleItemEditors();
    if (!editors.length) return [];

    let commonKeys = null;
    let labelByKey = {};

    editors.forEach((editor, index) => {
      const planButtons = Array.from(
        editor.querySelectorAll(`${SELECTORS.itemPanel} [data-subscription-option][data-selling-plan-id]:not([data-selling-plan-id=""])`)
      );
      const keySet = new Set();

      planButtons.forEach((planButton) => {
        const key = planButton.dataset.planKey;
        if (!key) return;
        keySet.add(key);
        if (index === 0) {
          labelByKey[key] = planButton.innerHTML;
        }
      });

      commonKeys = commonKeys === null ? keySet : new Set([...commonKeys].filter((key) => keySet.has(key)));
    });

    if (!commonKeys || !commonKeys.size) return [];
    return [...commonKeys].map((key) => ({ key, html: labelByKey[key] || key }));
  };

  const refreshDrawer = () => {
    // Der Sparplan-Rabatt senkt den Warenkorbwert: die tisso-app prüft danach den
    // Mindestbestellwert des Empfehlungsgeschenks (sie hört auf `cart-updated`).
    document.dispatchEvent(new CustomEvent('cart-updated'));
    if (typeof window.refreshCart === 'function') {
      window.refreshCart(false);
      return Promise.resolve();
    }
    return Promise.resolve();
  };

  const openInlineEditor = (button) => {
    const editor = button.closest(SELECTORS.itemEditor);
    const panel = editor?.querySelector(SELECTORS.itemPanel);
    if (!panel) return;
    button.hidden = true;
    panel.hidden = false;
  };

  const closeInlineEditor = (panel) => {
    const editor = panel.closest(SELECTORS.itemEditor);
    const trigger = editor?.querySelector(SELECTORS.itemToggle);
    panel.hidden = true;
    if (trigger) trigger.hidden = false;
  };

  const applySelection = async (panel, optionButton) => {
    const editor = panel.closest(SELECTORS.itemEditor);
    const button = editor?.querySelector(SELECTORS.itemToggle);
    const sellingPlanId = optionButton?.dataset.sellingPlanId || '';
    const planKey = optionButton?.dataset.planKey || '';
    if (!button) return;

    if (sellingPlanId && planKey) {
      updateItemEditorUsps(panel, planKey);
      updateItemEditorCommitmentNote(panel, planKey);
    }

    const line = parseInt(button.dataset.itemLine, 10);
    const quantity = parseInt(button.closest('[data-js-cart-item]')?.dataset.qty || '1', 10);
    if (!line) return;

    const loadingTarget = getLineItemLoadingTarget(panel, optionButton);

    setBusy(button, true);
    setLoadingOverlay(loadingTarget, true);
    try {
      await updateLineItem({
        line,
        quantity,
        sellingPlanId,
        variantId: button.dataset.variantId,
        subscriptionVariantId: button.dataset.subscriptionVariantId,
        itemKey: button.dataset.itemKey,
      });
      await refreshDrawer();
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingOverlay(loadingTarget, false);
      setBusy(button, false);
    }
  };

  const closeGlobalPanel = () => {
    const panel = document.querySelector(SELECTORS.globalPanel);
    const trigger = document.querySelector(SELECTORS.globalToggle);
    if (panel) panel.hidden = true;
    if (trigger) trigger.hidden = false;
  };

  const syncGlobalButtonVisibility = () => {
    const trigger = document.querySelector(SELECTORS.globalToggle);
    const wrapper = trigger?.closest('.subscription-cart');
    if (!trigger || !wrapper) return;

    const commonOptions = getCommonPlanOptions();
    wrapper.hidden = commonOptions.length === 0;
  };

  const openGlobalPanel = (button) => {
    const panel = document.querySelector(SELECTORS.globalPanel);
    const optionsContainer = panel?.querySelector(SELECTORS.globalOptions);
    if (!panel || !optionsContainer) return;

    const commonOptions = getCommonPlanOptions();
    if (!commonOptions.length) {
      const wrapper = button.closest('.subscription-cart');
      if (wrapper) wrapper.hidden = true;
      return;
    }

    optionsContainer.innerHTML = commonOptions
      .map(
        (option) => `<button type="button" class="subscription-modal-option" data-subscription-cart-option data-plan-key="${option.key}">${option.html}</button>`
      )
      .join('');

    button.hidden = true;
    panel.hidden = false;
  };

  const applyGlobalSelection = async (optionButton) => {
    const planKey = optionButton?.dataset.planKey;
    const trigger = document.querySelector(SELECTORS.globalToggle);
    if (!trigger) return;
    if (!planKey) return;

    const editors = getEligibleItemEditors();
    if (!editors.length) return;

    const globalOptionsTarget = optionButton.closest(SELECTORS.globalOptions);

    setBusy(trigger, true);
    setLoadingOverlay(globalOptionsTarget, true);
    try {
      // Process highest line first so removals don't shift lower indexes mid-loop
      const editorsByLine = editors
        .map((editor) => {
          const button = editor.querySelector(SELECTORS.itemToggle);
          const line = parseInt(button?.dataset.itemLine || '', 10);
          return { editor, button, line };
        })
        .filter((entry) => entry.line)
        .sort((a, b) => b.line - a.line);

      for (const { editor, button, line } of editorsByLine) {
        const quantity = parseInt(button?.closest('[data-js-cart-item]')?.dataset.qty || '1', 10);
        const targetPlan = editor.querySelector(
          `${SELECTORS.itemPanel} [data-subscription-option][data-plan-key="${planKey}"][data-selling-plan-id]:not([data-selling-plan-id=""])`
        );
        const sellingPlanId = targetPlan?.dataset.sellingPlanId;
        if (!sellingPlanId) continue;

        await updateLineItem({
          line,
          quantity,
          sellingPlanId,
          variantId: button.dataset.variantId,
          subscriptionVariantId: button.dataset.subscriptionVariantId,
          itemKey: button.dataset.itemKey,
        });
      }
      await refreshDrawer();
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingOverlay(globalOptionsTarget, false);
      setBusy(trigger, false);
      closeGlobalPanel();
    }
  };

  syncGlobalButtonVisibility();

  document.addEventListener('click', (event) => {
    const itemButton = event.target.closest(SELECTORS.itemToggle);
    if (itemButton) {
      event.preventDefault();
      openInlineEditor(itemButton);
      return;
    }

    const modalOption = event.target.closest(SELECTORS.modalOptionButton);
    if (modalOption) {
      event.preventDefault();
      const panel = modalOption.closest(SELECTORS.itemPanel);
      if (panel) {
        applySelection(panel, modalOption);
      }
      return;
    }

    const cancelButton = event.target.closest(SELECTORS.itemCancel);
    if (cancelButton) {
      event.preventDefault();
      const panel = cancelButton.closest(SELECTORS.itemPanel);
      if (panel) {
        closeInlineEditor(panel);
      }
      return;
    }

    const globalButton = event.target.closest(SELECTORS.globalToggle);
    if (globalButton) {
      event.preventDefault();
      openGlobalPanel(globalButton);
      return;
    }

    const globalOption = event.target.closest(SELECTORS.globalOptionButton);
    if (globalOption) {
      event.preventDefault();
      applyGlobalSelection(globalOption);
      return;
    }

    const globalCancel = event.target.closest(SELECTORS.globalCancel);
    if (globalCancel) {
      event.preventDefault();
      closeGlobalPanel();
    }
  });
})();
